import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import CepInput from '../../components/shared/CepInput';
import { maskPhone, maskCPF, maskCNPJ } from '../../utils/masks';
import { validateCPF, validateCNPJ } from '../../utils/validators';
import { ArrowLeft, Wheat, CheckCircle } from 'lucide-react';

export default function CadastroCliente() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tipoPessoa, setTipoPessoa] = useState('fisica'); // 'fisica' | 'juridica'
  const [docError, setDocError] = useState('');

  const [form, setForm] = useState({
    nome: '', email: '', password: '', confirmPassword: '',
    nome_empresa: '', cpf_cnpj: '', whatsapp: '',
    cep: '', logradouro: '', numero: '', bairro: '', cidade: '', uf: '', ponto_referencia: '',
  });

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleCepFound = (addr) => {
    if (addr) setForm(f => ({ ...f, logradouro: addr.logradouro, bairro: addr.bairro, cidade: addr.cidade, uf: addr.uf }));
  };

  const handleDocChange = (raw) => {
    const digits = raw.replace(/\D/g, '');
    if (tipoPessoa === 'fisica') {
      set('cpf_cnpj', maskCPF(raw));
      if (digits.length === 11) {
        setDocError(validateCPF(digits) ? '' : 'CPF inválido');
      } else {
        setDocError('');
      }
    } else {
      set('cpf_cnpj', maskCNPJ(raw));
      if (digits.length === 14) {
        setDocError(validateCNPJ(digits) ? '' : 'CNPJ inválido');
      } else {
        setDocError('');
      }
    }
  };

  const validateDoc = () => {
    const digits = form.cpf_cnpj.replace(/\D/g, '');
    if (tipoPessoa === 'fisica') {
      if (digits.length !== 11) { setDocError('CPF incompleto. Obrigatório'); return false; }
      if (!validateCPF(form.cpf_cnpj)) { setDocError('CPF inválido. Obrigatório'); return false; }
    } else {
      if (digits.length !== 14) { setDocError('CNPJ incompleto. Obrigatório'); return false; }
      if (!validateCNPJ(form.cpf_cnpj)) { setDocError('CNPJ inválido. Obrigatório'); return false; }
    }
    return true;
  };

  const handleNext = () => {
    if (!form.nome.trim()) { setError('Nome é obrigatório'); return; }
    if (!form.email.trim()) { setError('E-mail é obrigatório'); return; }
    if (!form.cpf_cnpj.trim()) { setDocError('Obrigatório'); setError(''); return; }
    if (!validateDoc()) { setError(''); return; }
    if (!form.password) { setError('Senha é obrigatória'); return; }
    if (form.password.length < 8) { setError('Senha deve ter no mínimo 8 caracteres'); return; }
    if (form.password !== form.confirmPassword) { setError('Senhas não conferem'); return; }
    setError('');
    setStep(2);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/register', {
        email: form.email,
        password: form.password,
        nome: form.nome,
        nome_empresa: form.nome_empresa,
        cpf_cnpj: form.cpf_cnpj,
        whatsapp: form.whatsapp,
        cep: form.cep,
        logradouro: form.logradouro,
        numero: form.numero,
        bairro: form.bairro,
        cidade: form.cidade,
        uf: form.uf,
        ponto_referencia: form.ponto_referencia,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.userMessage || 'Erro ao criar cadastro');
    } finally {
      setLoading(false);
    }
  };

  if (success) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-brand-bg px-6 text-center">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-5">
        <CheckCircle className="w-10 h-10 text-green-600" />
      </div>
      <h2 className="font-heading text-2xl text-brand-text font-bold mb-2">Cadastro realizado!</h2>
      <p className="text-brand-text-muted font-body text-sm mb-2">Sua conta foi criada com sucesso.</p>
      <p className="text-brand-text-muted font-body text-sm mb-8">Você já pode fazer login e realizar seus pedidos.</p>
      <button onClick={() => navigate('/login')} className="btn-primary py-3 px-8 rounded-full font-dashboard font-semibold text-sm" data-testid="ir-login-btn">
        Entrar agora
      </button>
    </div>
  );

  const inputCls = "input-mobile";

  return (
    <div className="min-h-screen bg-brand-bg px-4 py-6" data-testid="cadastro-page">
      <div className="max-w-sm mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/login" className="p-2 rounded-full hover:bg-brand-accent-light" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 text-brand-text" />
          </Link>
          <div>
            <h1 className="font-heading text-2xl font-bold text-brand-text">Criar Conta</h1>
            <p className="text-xs text-brand-text-muted font-body">Passo {step} de 2</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 bg-brand-border rounded-full mb-6">
          <div className="h-full bg-brand-primary rounded-full transition-all duration-300" style={{ width: `${step * 50}%` }} />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4" data-testid="cadastro-error">{error}</div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            {/* Tipo de Pessoa */}
            <div>
              <label className="block text-sm font-medium text-brand-text-muted mb-2 font-body">Tipo de Pessoa *</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => { setTipoPessoa('fisica'); set('cpf_cnpj', ''); setDocError(''); }}
                  className={`py-2.5 rounded-xl border-2 text-sm font-dashboard font-semibold transition-colors ${tipoPessoa === 'fisica' ? 'border-brand-primary bg-brand-accent-light text-brand-primary' : 'border-brand-border text-brand-text-muted'}`}
                  data-testid="tipo-fisica"
                >
                  Pessoa Física
                </button>
                <button
                  type="button"
                  onClick={() => { setTipoPessoa('juridica'); set('cpf_cnpj', ''); setDocError(''); }}
                  className={`py-2.5 rounded-xl border-2 text-sm font-dashboard font-semibold transition-colors ${tipoPessoa === 'juridica' ? 'border-brand-primary bg-brand-accent-light text-brand-primary' : 'border-brand-border text-brand-text-muted'}`}
                  data-testid="tipo-juridica"
                >
                  Pessoa Jurídica
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-brand-text-muted mb-1.5 font-body">
                {tipoPessoa === 'fisica' ? 'Nome Completo' : 'Nome do Responsável'} *
              </label>
              <input className={inputCls} value={form.nome} onChange={e => set('nome', e.target.value)} placeholder="Seu nome completo" data-testid="field-nome" />
            </div>

            {tipoPessoa === 'juridica' && (
              <div>
                <label className="block text-sm font-medium text-brand-text-muted mb-1.5 font-body">Razão Social / Nome da Empresa</label>
                <input className={inputCls} value={form.nome_empresa} onChange={e => set('nome_empresa', e.target.value)} placeholder="Nome da empresa" />
              </div>
            )}

            {/* CPF ou CNPJ */}
            <div>
              <label className="block text-sm font-medium text-brand-text-muted mb-1.5 font-body">
                {tipoPessoa === 'fisica' ? 'CPF' : 'CNPJ'} *
              </label>
              <input
                className={`${inputCls} ${docError ? 'border-red-400 focus:ring-red-300' : ''}`}
                value={form.cpf_cnpj}
                onChange={e => handleDocChange(e.target.value)}
                placeholder={tipoPessoa === 'fisica' ? '000.000.000-00' : '00.000.000/0000-00'}
                maxLength={tipoPessoa === 'fisica' ? 14 : 18}
                inputMode="numeric"
                data-testid="field-cpf-cnpj"
              />
              {docError && (
                <p className="text-xs text-red-600 mt-1 font-body" data-testid="doc-error">{docError}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-brand-text-muted mb-1.5 font-body">E-mail *</label>
              <input type="email" className={inputCls} value={form.email} onChange={e => set('email', e.target.value)} placeholder="seu@email.com" data-testid="field-email" />
            </div>

            <div>
              <label className="block text-sm font-medium text-brand-text-muted mb-1.5 font-body">WhatsApp</label>
              <input className={inputCls} value={form.whatsapp} onChange={e => set('whatsapp', maskPhone(e.target.value))} placeholder="(00) 00000-0000" />
            </div>

            <div>
              <label className="block text-sm font-medium text-brand-text-muted mb-1.5 font-body">Senha *</label>
              <input type="password" className={inputCls} value={form.password} onChange={e => set('password', e.target.value)} placeholder="Mín. 8 caracteres" data-testid="field-password" />
            </div>

            <div>
              <label className="block text-sm font-medium text-brand-text-muted mb-1.5 font-body">Confirmar Senha *</label>
              <input type="password" className={inputCls} value={form.confirmPassword} onChange={e => set('confirmPassword', e.target.value)} placeholder="Repita a senha" data-testid="field-confirm-password" />
            </div>

            <button onClick={handleNext} className="w-full py-3.5 rounded-full btn-primary font-dashboard font-semibold text-sm" data-testid="step1-next">
              Próximo
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-brand-text-muted mb-1.5 font-body">CEP *</label>
              <CepInput value={form.cep} onChange={v => set('cep', v)} onAddressFound={handleCepFound} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-text-muted mb-1.5 font-body">Logradouro *</label>
              <input className={inputCls} value={form.logradouro} onChange={e => set('logradouro', e.target.value)} placeholder="Rua, Av..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-brand-text-muted mb-1.5 font-body">Número *</label>
                <input className={inputCls} value={form.numero} onChange={e => set('numero', e.target.value)} placeholder="123" />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-text-muted mb-1.5 font-body">UF</label>
                <input className={inputCls} value={form.uf} onChange={e => set('uf', e.target.value)} placeholder="SP" maxLength={2} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-text-muted mb-1.5 font-body">Bairro</label>
              <input className={inputCls} value={form.bairro} onChange={e => set('bairro', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-text-muted mb-1.5 font-body">Cidade</label>
              <input className={inputCls} value={form.cidade} onChange={e => set('cidade', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-text-muted mb-1.5 font-body">Ponto de Referência</label>
              <input className={inputCls} value={form.ponto_referencia} onChange={e => set('ponto_referencia', e.target.value)} placeholder="Próximo ao mercado..." />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setStep(1)} className="flex-1 py-3.5 rounded-full border-2 border-brand-primary text-brand-primary font-dashboard font-semibold text-sm">Voltar</button>
              <button onClick={handleSubmit} disabled={loading} className="flex-1 py-3.5 rounded-full btn-primary font-dashboard font-semibold text-sm disabled:opacity-60" data-testid="submit-cadastro">
                {loading ? 'Enviando...' : 'Cadastrar'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
